package com.arena_web.ui.sections.info

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.isMobile
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPaddingTop
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mWidth
import com.arena_web.ui.utils.sBorderRadius
import com.arena_web.ui.utils.sFontSize
import com.arena_web.ui.utils.sGap
import com.arena_web.ui.utils.sHeight
import com.arena_web.ui.utils.sLetterSpacing
import com.arena_web.ui.utils.sLineHeight
import com.arena_web.ui.utils.sMarginTop
import com.arena_web.ui.utils.sMaxWidth
import com.arena_web.ui.utils.sPadding
import com.arena_web.ui.utils.sPaddingBottom
import com.arena_web.ui.utils.sPaddingTop
import com.arena_web.ui.utils.sSize
import com.arena_web.ui.utils.sWidth
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms

// Entrance: title and card rise in with a slight stagger.
// Final frame is `transform: none` so no GPU layer stays promoted.
val InfoRiseKeyframes = Keyframes {
    from {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(22px)")
        }
    }
    to {
        Modifier.styleModifier {
            property("opacity", "1")
            property("transform", "none")
        }
    }
}

@Composable
private fun Modifier.riseIn(delayMs: Int) = this.animation(
    InfoRiseKeyframes.toAnimation(
        duration = 550.ms,
        delay = delayMs.ms,
        timingFunction = AnimationTimingFunction.EaseOut,
        fillMode = AnimationFillMode.Both,
    )
)

/**
 * Shared layout for the static info pages (About, Terms of Service,
 * Privacy Policy): big page title and the content stacked inside a
 * gradient-bordered surface card.
 */
@Composable
fun InfoPageScaffold(
    title: String,
    content: @Composable ColumnScope.() -> Unit,
) {
    // Phones get the layout the mobile app ports 1:1 (StaticPageScreen):
    // plain content on the body background, no gradient card — the s()-scaled
    // desktop card is clamped to 0.4 behind isMobile() and renders wrong.
    if (isMobile()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .mPaddingTop(8)
                .mGap(24)
                .styleModifier {
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    // Clear the fixed bottom-nav pill.
                    property(
                        "padding-bottom",
                        "calc(${m(120)} + env(safe-area-inset-bottom, 0px))"
                    )
                },
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .mFontSize(34)
                    .mLineHeight(40)
                    .mLetterSpacing(1)
                    .fontWeight(AppTypography.SemiBold)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                    .styleModifier { property("color", "#FFFFFF") }
                    .riseIn(0)
            )
            Column(
                modifier = Modifier.fillMaxWidth().mGap(24).riseIn(120),
            ) {
                content()
            }
        }
        return
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sMaxWidth(920)
            .sPaddingTop(48)
            .sPaddingBottom(80)
            .sGap(28),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            title,
            modifier = Modifier
                .sFontSize(44)
                .sLineHeight(52)
                .sLetterSpacing(0.8)
                .fontWeight(AppTypography.Bold)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                .styleModifier { property("color", "#FFFFFF") }
                .riseIn(0)
        )

        GradientBorderBox(
            modifier = Modifier.fillMaxWidth().riseIn(120),
            borderRadius = 24,
            borderGradient = AppGradients.BorderInnerL,
            borderWidth = "1px",
            background = "${AppGradients.InteractiveSecondary}, rgba(8, 12, 28, 0.45)",
            contentAlignment = Alignment.TopStart,
        ) {
            // Padding lives on an inner column, never on GradientBorderBox itself
            // (the border ring is an absolutely-positioned child of the outer box).
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(44, 48)
                    .sGap(36),
            ) {
                content()
            }
        }
    }
}

@Composable
fun InfoSection(heading: String, content: @Composable ColumnScope.() -> Unit) {
    if (isMobile()) {
        Column(modifier = Modifier.mGap(10)) {
            Row(modifier = Modifier.mGap(10), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .mWidth(4)
                        .mHeight(18)
                        .mBorderRadius(2)
                        .flexShrink(0)
                        .styleModifier {
                            property("background", "linear-gradient(180deg, #0061FF, rgba(0, 97, 255, 0.35))")
                        }
                )
                SpanText(
                    heading,
                    modifier = Modifier
                        .mFontSize(18)
                        .mLineHeight(22)
                        .mLetterSpacing(0.5)
                        .fontWeight(AppTypography.SemiBold)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier { property("color", "#FFFFFF") }
                )
            }
            content()
        }
        return
    }

    Column(modifier = Modifier.sGap(14)) {
        Row(modifier = Modifier.sGap(12), verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .sWidth(4)
                    .sHeight(22)
                    .sBorderRadius(2)
                    .flexShrink(0)
                    .styleModifier {
                        property("background", "linear-gradient(180deg, #0061FF, rgba(0, 97, 255, 0.35))")
                    }
            )
            SpanText(
                heading,
                modifier = Modifier
                    .sFontSize(23)
                    .sLineHeight(29)
                    .sLetterSpacing(0.4)
                    .fontWeight(AppTypography.SemiBold)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                    .styleModifier { property("color", "#FFFFFF") }
            )
        }
        content()
    }
}

@Composable
fun InfoParagraph(text: String) {
    if (isMobile()) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(16)
                .mLineHeight(24)
                .mLetterSpacing(0.2)
                .color(AppColors.Text.Secondary)
                .fontWeight(AppTypography.Regular)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
        )
        return
    }
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(20)
            .sLineHeight(32)
            .sLetterSpacing(0.2)
            .color(AppColors.Text.Secondary)
            .fontWeight(AppTypography.Regular)
            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
    )
}

@Composable
fun InfoBullet(text: String) {
    if (isMobile()) {
        Row(modifier = Modifier.mGap(12), verticalAlignment = Alignment.Top) {
            Box(
                modifier = Modifier
                    .mSize(6)
                    .mBorderRadius(100)
                    .flexShrink(0)
                    .styleModifier {
                        property("margin-top", m(9))
                        property("background", "#0061FF")
                    }
            )
            SpanText(
                text,
                modifier = Modifier
                    .mFontSize(16)
                    .mLineHeight(24)
                    .mLetterSpacing(0.2)
                    .color(AppColors.Text.Secondary)
                    .fontWeight(AppTypography.Regular)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
        }
        return
    }
    Row(modifier = Modifier.sGap(12), verticalAlignment = Alignment.Top) {
        Box(
            modifier = Modifier
                .sSize(6)
                .sBorderRadius(100)
                .sMarginTop(10)
                .flexShrink(0)
                .styleModifier { property("background", "#0061FF") }
        )
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(20)
                .sLineHeight(32)
                .sLetterSpacing(0.2)
                .color(AppColors.Text.Secondary)
                .fontWeight(AppTypography.Regular)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
        )
    }
}
